
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { BarChart3, ChevronLeft, ChevronRight, TrendingUp, Info } from 'lucide-react';

interface WorkflowPredictionViewerProps {
  runId: string;
}

export const WorkflowPredictionViewer = ({ runId }: WorkflowPredictionViewerProps) => {
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data: rows, isLoading } = useQuery({
    queryKey: ['workflow-predictions', runId, page],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workflow_data')
        .select('*')
        .eq('workflow_run_id', runId)
        .eq('data_type', 'predicted')
        .order('row_index')
        .range(page * pageSize, (page + 1) * pageSize - 1);
      if (error) throw error;
      return data || [];
    },
  });

  const { data: totalCount } = useQuery({
    queryKey: ['workflow-predictions-count', runId],
    queryFn: async () => {
      const { count } = await supabase
        .from('workflow_data')
        .select('*', { count: 'exact', head: true })
        .eq('workflow_run_id', runId)
        .eq('data_type', 'predicted');
      return count || 0;
    },
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground py-8 text-center">Loading predictions…</p>;
  }

  if (!rows?.length) {
    return (
      <div className="py-12 text-center">
        <BarChart3 className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
        <p className="text-muted-foreground">No prediction data available for this run.</p>
      </div>
    );
  }

  const totalPages = Math.ceil((totalCount || 0) / pageSize);

  // Extract prediction-specific fields
  const getRowData = (row: any) => row.row_data as Record<string, unknown>;
  const identityFields = ['id', 'name', 'email', 'company'];
  const predictionFields = Object.keys(getRowData(rows[0])).filter(k => k.startsWith('_'));

  const getScoreColor = (score: number) => {
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 75) return 'High';
    if (score >= 50) return 'Medium';
    return 'Low';
  };

  const getExplanation = (data: Record<string, unknown>) => {
    const factors: string[] = [];
    if (data.status === 'customer') factors.push('existing customer relationship');
    if (data.email) factors.push('contact information available');
    if (data.company) factors.push('company identified');
    if ((data._lead_score as number) > 70) factors.push('strong engagement indicators');
    if (factors.length === 0) factors.push('general behavioral patterns');
    return `This prediction is based on: ${factors.join(', ')}.`;
  };

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card className="p-4 bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">About These Predictions</p>
            <p className="text-sm text-blue-700 dark:text-blue-400">
              Each record has been scored by AI based on available CRM data. Scores indicate the likelihood of conversion or engagement. 
              Higher scores suggest stronger leads or higher-value customers.
            </p>
          </div>
        </div>
      </Card>

      {/* Score Distribution Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'High Potential', min: 75, color: 'text-green-600' },
          { label: 'Medium Potential', min: 50, max: 74, color: 'text-yellow-600' },
          { label: 'Needs Attention', max: 49, color: 'text-red-600' },
        ].map((bucket) => {
          const count = rows.filter(r => {
            const score = (getRowData(r)._lead_score as number) || 0;
            if (bucket.min && bucket.max) return score >= bucket.min && score <= bucket.max;
            if (bucket.min) return score >= bucket.min;
            return score <= (bucket.max || 0);
          }).length;
          return (
            <Card key={bucket.label} className="p-4 text-center">
              <p className={`text-2xl font-semibold ${bucket.color}`}>{count}</p>
              <p className="text-sm text-muted-foreground">{bucket.label}</p>
            </Card>
          );
        })}
      </div>

      {/* Records Table */}
      <ScrollArea className="w-full">
        <div className="min-w-[700px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">#</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Lead Score</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead className="min-w-[250px]">Why This Score</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(row => {
                const data = getRowData(row);
                const score = (data._lead_score as number) || 0;
                const confidence = ((data._confidence as number) || 0) * 100;
                return (
                  <TableRow key={row.id}>
                    <TableCell className="text-xs text-muted-foreground">{row.row_index + 1}</TableCell>
                    <TableCell className="font-medium text-sm">{String(data.name || '—')}</TableCell>
                    <TableCell className="text-sm">{String(data.company || '—')}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold text-sm ${getScoreColor(score)}`}>{score}</span>
                        <Badge variant="outline" className={`text-[10px] ${getScoreColor(score)}`}>
                          {getScoreLabel(score)}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-[100px]">
                        <Progress value={confidence} className="h-1.5 flex-1" />
                        <span className="text-xs text-muted-foreground">{Math.round(confidence)}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {getExplanation(data)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </ScrollArea>

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-muted-foreground">Page {page + 1} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
